import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { currentBranding } from '../../config/branding';
import { branding } from '../../config/enum';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import BatteryLoader from '../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../shared-components/button/Button';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import RadioButton from '../../shared-components/radio-button/RadioButton';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import profile from '../../utils/api/profile';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Payout drop down content
 * @returns JSX.Element
 */
function Payout() {
  const { t } = useTranslation('setting');
  const dispatch = useAppDispatch();
  const [payoutPreference, setPayoutPreference] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [lastSavedPreference, setLastSavedPreference] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [stripeId, setStripeID] = useState('');
  const [payoutEnabled, setPayoutEnabled] = useState(false);
  const [isFromStripeSettings, setIsFromStripeSettings] = useState(false);
  const [canProceed, setCanProceed] = useState(true); // NEW: controls the Proceed button visibility
  const [loading, setLoading] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });
  const isFacilityTest = window.location.href.includes('https://facility-test');

  /**
   * Update payout enabled status
   * @param {boolean} enabled - New payout status
   */
  const performPayoutStatusUpdate = (enabled: boolean) => {
    setLoading(true);
    profile
      .updatePayoutEnabled({ isStripePayoutEnabled: enabled })
      .then((response) => {
        if (response?.status === 200 || response?.success) {
          setPayoutEnabled(enabled);
          setModalBoxDetail({
            title: '',
            content: (
              <SuccessNotification
                title={t('statusChanged')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {
                  setModalBoxStatus('close');
                }}
              />
            ),
            notificationModal: true,
            header: false,
            /**
             * Handle Payout Enabled toggle
             */
            closeModal: () => {
              setModalBoxStatus('close');
            },
          });
          setModalBoxStatus('open');
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      })
      .finally(() => setLoading(false));
  };
  /**
   * Handle payout enabled toggle
   * @param {boolean} enabled - New payout status
   */
  const handlePayoutEnabled = (enabled: boolean) => {
    if (!enabled) {
      setModalBoxDetail({
        title: '',
        content: (
          <DeleteWarning
            selectedRowId={stripeId}
            title={t('disableStripeTitle')}
            description={t('disableStripeDesc')}
            cancelButtonOnClick={() => setModalBoxStatus('close')}
            submitButtonLabel={t('disable')}
            submitButtonOnClick={() => {
              setModalBoxStatus('close');
              performPayoutStatusUpdate(enabled);
            }}
          />
        ),
        notificationModal: true,
        header: false,

        /**
         * closeModal Enabled toggle
         */
        closeModal: () => setModalBoxStatus('close'),
      });
      setModalBoxStatus('open');
    } else {
      performPayoutStatusUpdate(enabled);
    }
  };
  /**
   * Fetch Stripe settings
   */
  const fetchStripeSettings = () => {
    setLoading(true);
    profile
      .getStripeSettings()
      .then((response) => {
        if (response?.data) {
          const preference = response.data.payoutSchedulePreference;
          const stripeAccountId = response.data.stripeConnectAccountId;
          if (
            preference &&
            stripeAccountId &&
            Object.keys(response.data)?.includes('isStripePayoutEnabled')
          ) {
            setPayoutPreference(preference);
            setLastSavedPreference(preference);
            setIsFromStripeSettings(true);
            setStripeID(response.data.stripeConnectAccountId);
            setPayoutEnabled(response.data.isStripePayoutEnabled);
          }
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      })
      .finally(() => setLoading(false));
  };
  /**
   * Update Stripe payout preference
   */
  const handleStripeUpdatePayout = () => {
    setLoading(true);
    profile
      .updatePayout({ payoutSchedulePreference: payoutPreference })
      .then((response) => {
        if (response?.status === 200 || response?.success) {
          setLastSavedPreference(payoutPreference);
          setHasChanges(false);
          setIsPaymentSuccess(true);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      })
      .finally(() => setLoading(false));
  };

  /**
   * Handle save changes
   */
  const handleSaveChanges = () => {
    handleStripeUpdatePayout();
    setHasChanges(false);
  };

  /**
   * Handle proceed to pay
   */
  const handleProceed = async () => {
    if (!payoutPreference) {
      setHasError(true);
      return;
    }

    setLastSavedPreference(payoutPreference);
    setHasChanges(false);
    setLoading(true);

    try {
      const response = await profile.createPayout({ payoutSchedulePreference: payoutPreference });
      if (response?.data?.url) {
        setCanProceed(false);
        window.location.href = response.data.url;
      } else {
        setCanProceed(true);
      }
    } catch (error: any) {
      setCanProceed(true);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const changed = isFromStripeSettings && payoutPreference !== lastSavedPreference;
    setHasChanges(changed);
  }, [payoutPreference, lastSavedPreference, isFromStripeSettings]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (hasError) {
      timer = setTimeout(() => setHasError(false), 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [hasError]);

  useEffect(() => {
    fetchStripeSettings();
  }, []);

  return loading ? (
    <div className="table-loading">
      <BatteryLoader />
    </div>
  ) : (
    <div className="payout-container">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      <div className="payout-top">
        {isFromStripeSettings && (
          <div className="paymentSuccess">
            <p className="strip-title">
              {t('stripePayId')}: <span className="id">{stripeId}</span>
            </p>
            <p className="strip-title">
              {t('stripePayout')}:
              <span className="switch">
                <SwitchButton
                  change={(checked) => {
                    handlePayoutEnabled(checked);
                  }}
                  checked={payoutEnabled}
                />
              </span>
            </p>
          </div>
        )}

        <div className="text-group">
          <p>
            {t('selectPayoutRecurrence')}
            {canProceed && !isPaymentSuccess && !isFromStripeSettings && (
              <span className="required">*</span>
            )}
          </p>
          <p className="description">{t('payoutDescription')}</p>
        </div>

        <RadioButton
          name="payoutRecurrence"
          value={payoutPreference}
          list={[{ label: t('monthly'), value: 'monthly' }]}
          onChangeValue={() => setPayoutPreference('monthly')}
        />
        <RadioButton
          name="payoutRecurrence"
          value={payoutPreference}
          list={[{ label: t('quarterly'), value: 'quarterly' }]}
          onChangeValue={() => setPayoutPreference('quarterly')}
        />
        {isFacilityTest && (
          <>
            <RadioButton
              name="payoutRecurrence"
              value={payoutPreference}
              list={[{ label: t('oneHour'), value: 'one-hour' }]}
              onChangeValue={() => setPayoutPreference('one-hour')}
            />
            <RadioButton
              name="payoutRecurrence"
              value={payoutPreference}
              list={[{ label: t('twoHour'), value: 'two-hour' }]}
              onChangeValue={() => setPayoutPreference('two-hour')}
            />
            <RadioButton
              name="payoutRecurrence"
              value={payoutPreference}
              list={[{ label: t('threeHour'), value: 'three-hour' }]}
              onChangeValue={() => setPayoutPreference('three-hour')}
            />
          </>
        )}
      </div>

      <div className="payout-bottom">
        {!payoutPreference && (
          <div className="text-group">
            <p>{t('setupStripePayout')}</p>
            <p className="description">{t('setupStripeDesc')}</p>
            {currentBranding === branding.VOLTIE && <p className="warning">{t('voltWarning')}</p>}
          </div>
        )}

        <div className="button">
          {canProceed && !isPaymentSuccess && !isFromStripeSettings && (
            <div className={payoutPreference ? 'active' : ''}>
              <MyButton
                label={t('proceed')}
                buttonType="back"
                disableHoverEffect
                onClickFunc={handleProceed}
              />
            </div>
          )}

          {hasChanges && (
            <div className={payoutPreference ? 'active' : ''}>
              <MyButton
                label={t('saveChanges')}
                buttonType="back"
                disableHoverEffect
                onClickFunc={handleSaveChanges}
              />
            </div>
          )}

          {hasError && <span className="error">{t('payoutRequired')}</span>}
        </div>
        {isFromStripeSettings && <p className="error-period">{t('cannotChangePeriod')}</p>}
      </div>
    </div>
  );
}

export default Payout;
